import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { addImageURL, addProductURL } from '../../components/assets/constants/Urls/Url';
import axios from 'axios';
import './AddProduct.css';
import { useNavigate } from 'react-router-dom';
import Category from '../../hooks/categorycall';
import Brand from '../../hooks/brandCalls';
import Quill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const FormInput = ({ label, type = 'text', name, value, onChange, placeholder, min = 0 }) => (
  <div className="mb-3">
    <label className="form-label">{label}</label>
    <input
      type={type}
      className="form-control"
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      min={type === 'number' ? min : undefined}
    />
  </div>
);

function AddProduct() {
  const navigate = useNavigate();
  const { categoryData } = Category();
  const { brandData } = Brand();
  const [isLoading, setIsLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [editorHeight, setEditorHeight] = useState(150);

  const [product, setProduct] = useState({
    name: '',
    status: 'In Stock',
    category: 'select',
    brand: 'select',
    type: 'Physical',
    price: 0,
    costPerItem: 0,
    description: '',
    images: [],
    trackQuantity: true,
    maxOrderQuantity: 0,
    sku: '',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProduct({
      ...product,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
    setProduct((prevProduct) => ({
      ...prevProduct,
      description: value,
    }));
    const lineCount = value.split('\n').length;
    const newHeight = Math.min(150 + lineCount * 20, 400);
    setEditorHeight(newHeight);
  };

  const handleCategoryChange = (e) => {
    setProduct({
      ...product,
      category: parseInt(e.target.value, 10),
    });
  };

  const handleBrandChange = (e) => {
    setProduct({
      ...product,
      brand: parseInt(e.target.value, 10),
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    })); // Reverse here
  
    setProduct((prevProduct) => ({
      ...prevProduct,
      images: [...prevProduct.images, ...newImages],
    }));
  };
  

  const removeImage = (index) => {
    const newImages = product.images.filter((_, i) => i !== index);
    setProduct({ ...product, images: newImages });
  };

  const handleSubmit = async (e) => {
    setIsLoading(true);
    e.preventDefault();
    try {
      if (product.images.length > 0 && product.price > 0) {
        const productRes = await axios.post(addProductURL, {
          name: product.name,
          description: product.description,
          price: product.price,
          category: product.category,
          brand: product.brand,
          status: product.status === 'In Stock',
        });

        const formData = new FormData();
        product.images.forEach((imageObj) => {
          if (imageObj.file) {
            formData.append('images', imageObj.file);
          }
        });

        await axios.post(addImageURL + `${productRes.data.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        navigate('/admins');
      }
    } catch (error) {
      console.error('Error:', error.response ? error.response.data : error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mt-5">
      <h3 className="mb-4">Add Product</h3>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Images</label>
          <input
            type="file"
            className="form-control"
            accept="image/*"
            multiple
            onChange={handleImageChange}
          />
          <div className="mt-3 d-flex flex-wrap">
            {product.images.map((imageObj, index) => (
              <div key={index} className="me-2 mb-2 position-relative">
                <img
                  src={imageObj.preview}
                  alt={`Product ${index + 1}`}
                  className="img-thumbnail"
                  style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                />
                <button
                  type="button"
                  className="btn btn-danger btn-sm position-absolute top-0 end-0"
                  onClick={() => removeImage(index)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <FormInput
          label="Name"
          name="name"
          value={product.name}
          onChange={handleChange}
          placeholder="Item name"
        />
        <div className="mb-3">
          <label className="form-label">Status</label>
          <select
            className="form-control"
            name="status"
            value={product.status}
            onChange={handleChange}
          >
            <option value="In Stock">In Stock</option>
            <option value="Out of Stock">Out of Stock</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Category</label>
          <select
            className="form-control"
            name="category"
            value={product.category}
            onChange={handleCategoryChange}
          >
            <option value="select" disabled>
              Select Category
            </option>
            {categoryData.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Brand</label>
          <select
            className="form-control"
            name="brand"
            value={product.brand}
            onChange={handleBrandChange}
          >
            <option value="select" disabled>
              Select Brand
            </option>
            {brandData.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.brand_name}
              </option>
            ))}
          </select>
        </div>
        <FormInput
          label="Price ($)"
          type="number"
          name="price"
          value={product.price}
          onChange={handleChange}
          min={0}
        />
        <FormInput
          label="Cost per Item ($)"
          type="number"
          name="costPerItem"
          value={product.costPerItem}
          onChange={handleChange}
          min={0}
        />
        <Quill
          theme="snow"
          value={description}
          onChange={handleDescriptionChange}
          style={{ height: `${editorHeight}px`, overflowY: 'auto' }}
        />
        <FormInput
          label="SKU"
          name="sku"
          value={product.sku}
          onChange={handleChange}
          placeholder="SKU"
        />
        <div className="form-check mb-3">
          <input
            type="checkbox"
            className="form-check-input"
            name="trackQuantity"
            checked={product.trackQuantity}
            onChange={handleChange}
          />
          <label className="form-check-label">Track Quantity</label>
        </div>
        {isLoading ? (
          "Loading..."
        ) : (
          <button type="submit" className="btn btn-primary">
            Add Item
          </button>
        )}
      </form>
    </div>
  );
}

export default AddProduct;
